/**
 * DeleteTransactionModal Component
 * 
 * Modal for confirming transaction deletion.
 * 
 * Features:
 * - Modal overlay with focus trap
 * - Simple confirmation dialog
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 */

import React, { useEffect, useRef } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import type { Transaction } from '../../types/transaction.types';

interface DeleteTransactionModalProps {
  isOpen: boolean;
  transaction: Transaction | null;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

export function DeleteTransactionModal({ 
  isOpen, 
  transaction,
  onClose, 
  onConfirm,
  loading = false
}: DeleteTransactionModalProps): React.ReactElement | null {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Focus trap and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the cancel button
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !transaction) return null;

  const displayDescription = transaction.merchantName || transaction.description || 'this transaction';
  const displayAmount = transaction.amount ? 
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Math.abs(transaction.amount)) : 
    '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-transaction-title"
      aria-describedby="delete-transaction-description"
    >
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <h2 id="delete-transaction-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Delete Transaction
            </h2>
          </div>
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            disabled={loading}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p id="delete-transaction-description" className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </p>
          
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {displayDescription}
              </p>
              {displayAmount && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {displayAmount}
                </p>
              )}
              {transaction.date && (
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {new Date(transaction.date).toLocaleDateString('en-AU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            ref={confirmButtonRef}
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Deleting...' : 'Delete Transaction'}
          </button>
        </div>
      </div>
    </div>
  );
}

